import { Link as RadixNavigationMenuLink } from '@radix-ui/react-navigation-menu'
import { cn } from '@seen/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type MouseEventHandler,
  useCallback,
} from 'react'
import { NavLink } from 'react-router'
import { useAddFlowNodeFromUrl } from '#src/hooks/useAddFlowNodeFromUrl.ts'
import { useIsInDesk } from '#src/hooks/useIsInDesk.ts'
import { shouldReloadApp } from '#src/utils/shouldReloadApp.ts'

const variants = cva('', {
  variants: {
    variant: {
      default:
        'block font-medium text-sm opacity-40 transition-colors hover:opacity-100 [&.active]:opacity-100',
      ghost: 'flex items-center gap-2 rounded-md',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface NavigationMenuLinkProps extends ComponentPropsWithoutRef<
  typeof NavLink
> {
  variant?: VariantProps<typeof variants>['variant']
}

export const NavigationMenuLink = forwardRef<
  ElementRef<typeof NavLink>,
  NavigationMenuLinkProps
>(({ to, onClick, target, className, variant, ...delegated }, ref) => {
  const addFlowNode = useAddFlowNodeFromUrl(to)
  const isInDesk = useIsInDesk()
  const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      if (
        addFlowNode &&
        !e.metaKey // We want cmd+click to open in new tab
      ) {
        e.preventDefault()
        addFlowNode()
      }
      onClick?.(e)
    },
    [addFlowNode, onClick]
  )
  return (
    <RadixNavigationMenuLink asChild>
      <NavLink
        to={to}
        ref={ref}
        reloadDocument={shouldReloadApp()}
        target={target ?? (isInDesk ? '_blank' : undefined)}
        className={cn(variants({ variant, className }))}
        onClick={handleClick}
        {...delegated}
      />
    </RadixNavigationMenuLink>
  )
})

NavigationMenuLink.displayName = RadixNavigationMenuLink.displayName
